import { useState } from 'react'
import { Card, CardBody } from '@/design-system/components/Card'
import { Button } from '@/design-system/components/Button'
import { Badge } from '@/design-system/components/Badge'
import { StatCard } from '@/design-system/components/StatCard'
import { usePendingPayouts } from './hooks/useAdmin'
import type { PendingPayout } from './hooks/useAdmin'

// Mock history data
const PAYOUT_HISTORY = [
  { id: 'H-001', photographer_name: 'Anna Lindqvist', amount: 3000, currency: 'SEK', processed_at: '2024-03-10T14:00:00Z', method: 'Swish' },
  { id: 'H-002', photographer_name: 'Jonas Eriksson', amount: 8000, currency: 'SEK', processed_at: '2024-03-08T11:30:00Z', method: 'Swish' },
  { id: 'H-003', photographer_name: 'Maja Karlsson', amount: 1500, currency: 'SEK', processed_at: '2024-03-05T09:00:00Z', method: 'Swish' },
  { id: 'H-004', photographer_name: 'Lena Pettersson', amount: 4500, currency: 'SEK', processed_at: '2024-03-01T16:20:00Z', method: 'Swish' },
]

export function PayoutManagement() {
  const { data: pending, isLoading, approvePayout, rejectPayout } = usePendingPayouts()
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending')
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const totalPending = pending.reduce((sum, p) => sum + p.amount, 0)

  const formatCurrency = (v: number, currency = 'SEK') =>
    new Intl.NumberFormat('sv-SE', { style: 'currency', currency, maximumFractionDigits: 0 }).format(v)

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' })

  const handleApprove = async (p: PendingPayout) => {
    setConfirmId(p.id)
    await approvePayout(p.id)
    setConfirmId(null)
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Utbetalningar</h1>
        <p className="text-sm text-text-secondary mt-1">Hantera utbetalningsförfrågningar</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Väntande utbetalningar"
          value={pending.length}
          loading={isLoading}
        />
        <StatCard
          label="Total väntande"
          value={formatCurrency(totalPending)}
          loading={isLoading}
        />
        <StatCard
          label="Genomförda denna månad"
          value={PAYOUT_HISTORY.length}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-border">
        {(['pending', 'history'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-brand-accent text-brand-accent'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab === 'pending' ? `Väntande (${pending.length})` : 'Historik'}
          </button>
        ))}
      </div>

      {activeTab === 'pending' && (
        <Card>
          {isLoading ? (
            <CardBody>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-16 bg-surface-overlay rounded-lg animate-pulse mb-3" />
              ))}
            </CardBody>
          ) : pending.length === 0 ? (
            <CardBody>
              <div className="py-10 text-center">
                <p className="text-3xl mb-3">✅</p>
                <p className="text-sm font-medium text-text-primary">Inga väntande utbetalningar</p>
                <p className="text-xs text-text-muted mt-1">Alla utbetalningar är behandlade</p>
              </div>
            </CardBody>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border">
                    {['Fotograf', 'Belopp', 'Netto (efter skatt)', 'Swish', 'Begärd', 'Saldo', ''].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {pending.map(p => (
                    <tr key={p.id} className="hover:bg-surface-overlay/50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="font-medium text-text-primary">{p.photographer_name}</p>
                        <p className="text-xs text-text-muted font-mono mt-0.5">{p.user_id}</p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-text-primary tabular-nums">
                          {formatCurrency(p.amount, p.currency)}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-text-secondary tabular-nums">
                          {formatCurrency(p.net_amount, p.currency)}
                        </p>
                        <p className="text-xs text-text-muted">30% källskatt</p>
                      </td>
                      <td className="px-5 py-4">
                        <span className="font-mono text-xs text-text-secondary">{p.swish_number}</span>
                      </td>
                      <td className="px-5 py-4 text-text-secondary whitespace-nowrap">
                        {formatDate(p.created_at)}
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-text-secondary tabular-nums">{formatCurrency(p.wallet_balance)}</p>
                        {p.amount > p.wallet_balance && (
                          <Badge variant="urgent">Otillräckligt</Badge>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="primary"
                            size="sm"
                            loading={confirmId === p.id}
                            onClick={() => handleApprove(p)}
                          >
                            Godkänn
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => rejectPayout(p.id)}
                          >
                            Avvisa
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'history' && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-border">
                  {['Fotograf', 'Belopp', 'Metod', 'Datum', 'Status'].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {PAYOUT_HISTORY.map(h => (
                  <tr key={h.id} className="hover:bg-surface-overlay/50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-text-primary">{h.photographer_name}</td>
                    <td className="px-5 py-3.5 font-medium text-text-primary tabular-nums">
                      {formatCurrency(h.amount, h.currency)}
                    </td>
                    <td className="px-5 py-3.5 text-text-secondary">{h.method}</td>
                    <td className="px-5 py-3.5 text-text-secondary whitespace-nowrap">{formatDate(h.processed_at)}</td>
                    <td className="px-5 py-3.5">
                      <Badge variant="complete">Genomförd</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  )
}
